// This component handles fetching data and business logic for the Menu Preview.

import { DraftCafeStation, DraftMenu, ICafeListItemViewModel, IMenuItemDb } from "@dining/api-client";
import { DayOfWeek } from "@fluentui/react";
import { createSelector } from "@reduxjs/toolkit";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { MenuPublishedState, enumIterator } from "../../common/commonConstants";
import { fetchCafeAllergenBank, fetchCafeMenusForTheWeek, setNotification } from "../../reducers/diningReducer";
import {
  cafeSelector,
  menuItemBankSelector,
  weekMenuSelector,
  weekStartStringSelector,
} from "../../selectors/diningSelector";
import { useAppDispatch } from "../../store/store";
import withAppInsights from "../AppInsights/AppInsights";
import MenuPreviewView from "./MenuPreviewView";
import { PreviewMenu, PreviewMenuItem } from "./types/menuPreviewTypes";

const MenuPreview = () => {
  const dispatch = useAppDispatch();
  const selectedCafe: ICafeListItemViewModel | undefined = useSelector(cafeSelector);
  const weekStart = useSelector(weekStartStringSelector);
  const menuItemBank = useSelector(menuItemBankSelector);

  // Fetches a week's worth of menus on initalization
  useEffect(() => {
    selectedCafe && dispatch(fetchCafeMenusForTheWeek(selectedCafe.id, weekStart));
    dispatch(setNotification(undefined));
  }, [selectedCafe, weekStart]);

  useEffect(() => {
    selectedCafe && dispatch(fetchCafeAllergenBank(selectedCafe.id));
  }, [selectedCafe]);
  const weeklyMenuSelector = createSelector(weekMenuSelector, (weeklyMenu) => {
    const menusByDay: Array<PreviewMenu[]> = enumIterator(DayOfWeek).map(() => {
      return [];
    });
    let unpublishedChangesPresent: boolean = false;

    if (weeklyMenu) {
      weeklyMenu?.stations?.forEach((station: DraftCafeStation) => {
        const initialDictionary: { [id: number]: PreviewMenuItem[] } = {};
        const formattedItemsDictionary: { [id: number]: PreviewMenuItem[] } | undefined = station.menu?.reduce(
          (result, item: DraftMenu) => {
            const rawData: IMenuItemDb | undefined = menuItemBank.find((entry) => item.menuItemId === entry.id);
            const itemWithData: PreviewMenuItem = {
              ...item,
              itemData: rawData,
            };
            if (!itemWithData.isPublished) unpublishedChangesPresent = true;
            if (rawData && Array.isArray(item.weekDays)) {
              item.weekDays.forEach((value) => {
                result[DayOfWeek[value]] = result[DayOfWeek[value]] ?? [];
                result[DayOfWeek[value]].push(itemWithData);
              });
            }
            return result;
          },
          initialDictionary,
        );
        if (formattedItemsDictionary) {
          Object.keys(formattedItemsDictionary)
            .filter((key) => !isNaN(Number(key)))
            .map((v) => Number(v))
            .forEach((dayNumber) => {
              menusByDay[dayNumber].push({
                cafeId: selectedCafe?.id ?? "",
                stationId: station.id ?? "",
                stationName: station.name,
                name: station.name,
                items: formattedItemsDictionary[dayNumber],
              });
            });
        }
      });
    }
    if (unpublishedChangesPresent) {
      dispatch(setNotification(MenuPublishedState.UNPUBLISHED_CHANGES));
    }
    return menusByDay;
  });

  const selectedWeeklyMenu: Array<PreviewMenu[]> = useSelector(weeklyMenuSelector);

  if (selectedCafe && selectedWeeklyMenu) {
    return <MenuPreviewView selectedCafe={selectedCafe} weeklyMenu={selectedWeeklyMenu} />;
  } else {
    return <div />;
  }
};

export default withAppInsights(MenuPreview);
